import { useLocalization } from '@components/Localization'
import {
  Container,
  FilterMenuCheckbox,
  FilterMenuRadio,
  FilterShowMoreButton,
  LoadingBar,
  Text,
  theme
} from '@gassan-ui'
import {
  Filters_filters_articleAttributeFilters,
  Filters_filters_brandCollectionFilters,
  Filters_filters_brandFilters
} from '@graphql/__generated__/Filters'
import { rem } from 'polished'
import React, { FC, useState } from 'react'
import { CombinedError } from 'urql'
import { FilterAction, FilterState } from '../reducer'

type Props = Omit<FilterState, 'page'> & {
  isLoading: boolean
  areProductsLoading: boolean
  attributes: Filters_filters_articleAttributeFilters[]
  brandOptions: Filters_filters_brandFilters[]
  collectionOptions: Filters_filters_brandCollectionFilters[]
  error?: CombinedError
  totalProducts?: number
  dispatch: React.Dispatch<FilterAction>
}

const FilterBar: FC<Props> = ({
  isLoading,
  areProductsLoading,
  attributes,
  brandOptions,
  collectionOptions,
  brands,
  collections,
  error,
  dispatch,
  totalProducts,
  filters,
  sort
}) => {
  const priceCategory = attributes.length
    ? attributes.filter(item => item.attributeName === 'priceCategory')[0]
    : null
  const [showAll, setShowAll] = useState(false)
  const { lang, messages } = useLocalization()
  return (
    <Container
      pt={['2rem']}
      display={{ _: 'none', large: 'flex' }}
      borderTop={`1px solid ${theme.colors.shades[200]}`}
      alignItems="center"
      flexWrap="wrap"
    >
      {isLoading && attributes.length === 0 ? (
        [...Array(6)].map((u, i) => (
          <LoadingBar key={`loading-${i}`} my="0.5rem" height={rem(26)} width="6rem" mr="2rem" />
        ))
      ) : error ? null : (
        <>
          {areProductsLoading ? (
            <LoadingBar mb="1rem" height={rem(26)} width="5rem" mr="2rem" />
          ) : (
            <Text color="shades.500" mb="1rem" mr="2.25rem" minWidth="5rem">
              {totalProducts &&
                `${totalProducts} ${messages.global[totalProducts > 1 ? 'products' : 'product']}`}
            </Text>
          )}
          <FilterMenuRadio
            items={[
              {
                label: messages.filters.popularity,
                value: 'default'
              },
              {
                label: messages.filters.newestFirst,
                value: 'new'
              },
              {
                label: messages.filters.priceASC,
                value: 'priceASC'
              },
              {
                label: messages.filters.priceDESC,
                value: 'priceDESC'
              }
            ]}
            label={messages.filters.sort}
            value={sort}
            onChange={sort => dispatch({ type: 'set-sort', sort })}
          />
          {brandOptions.length > 0 && (
            <FilterMenuCheckbox
              items={brandOptions.map(brand => ({
                label: brand.brand,
                value: brand.merk_nr
              }))}
              label={messages.filters.brands}
              activeFilters={brands.length}
              lang={lang}
              value={brands}
              onClickReset={() => dispatch({ type: 'reset-brands' })}
              onSelectItem={value =>
                dispatch({
                  type: 'set-brands',
                  value: value as number[]
                })
              }
            />
          )}
          {collectionOptions.length > 0 && (
            <FilterMenuCheckbox
              items={collectionOptions.map(collection => ({
                label: `${collection.collection}`,
                value: collection.collectionNumber
              }))}
              label={messages.filters.collection}
              activeFilters={collections.length}
              lang={lang}
              value={collections}
              onClickReset={() => dispatch({ type: 'reset-collections' })}
              onSelectItem={value =>
                dispatch({
                  type: 'set-collections',
                  value: value as number[]
                })
              }
            />
          )}
          {priceCategory && (
            <FilterMenuCheckbox
              items={priceCategory.articleFilterAttributeValues.map(item => ({
                label: item.attributeValue,
                value: item.attributeValueID
              }))}
              label={messages.filters.price}
              activeFilters={
                filters[priceCategory.attributeID] && filters[priceCategory.attributeID].length
              }
              lang={lang}
              value={filters[priceCategory.attributeID] ? filters[priceCategory.attributeID] : []}
              onClickReset={() =>
                dispatch({ type: 'reset-filter', key: priceCategory.attributeID })
              }
              onSelectItem={value =>
                dispatch({
                  type: 'set-filter',
                  key: priceCategory.attributeID,
                  value: value as number[]
                })
              }
            />
          )}
          {attributes
            .filter(
              filter =>
                filter.attributeName !== 'priceCategory' &&
                filter.articleFilterAttributeValues !== null
            )
            .slice(0, !showAll ? 5 : 99)
            .map(filter => (
              <FilterMenuCheckbox
                key={filter.attributeID}
                items={filter.articleFilterAttributeValues.map(item => ({
                  label: item.attributeValue,
                  value: item.attributeValueID
                }))}
                activeFilters={filters[filter.attributeID] && filters[filter.attributeID].length}
                label={filter.attributeName}
                value={filters[filter.attributeID] ? filters[filter.attributeID] : []}
                onSelectItem={value =>
                  dispatch({
                    type: 'set-filter',
                    key: filter.attributeID,
                    value: value as number[]
                  })
                }
                lang={lang}
                onClickReset={() => dispatch({ type: 'reset-filter', key: filter.attributeID })}
              ></FilterMenuCheckbox>
            ))}
          {!showAll && attributes.length > 5 && (
            <FilterShowMoreButton onClick={() => setShowAll(true)}>
              {lang === 'nl' ? 'Alle filters' : 'All filters'}
            </FilterShowMoreButton>
          )}
        </>
      )}
    </Container>
  )
}

export default FilterBar
